import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check } from "lucide-react";

const STEPS = ["Fabric", "Garment", "Measurements", "Style", "Pricing", "Checkout"];
const GARMENTS = [
  { k: "shirt", label: "Shirt", base: 3500 },
  { k: "blazer", label: "Blazer", base: 12000 },
  { k: "suit", label: "Suit", base: 22000 },
  { k: "trousers", label: "Trousers", base: 4500 },
  { k: "ethnic", label: "Ethnic", base: 9000 },
];
const STYLE_OPTIONS = {
  fit: ["Slim", "Tailored", "Relaxed"],
  collar: ["Spread", "Cutaway", "Button-Down", "Band"],
  cuff: ["Single Button", "Double", "French"],
  button: ["Mother of Pearl", "Horn", "Brass"],
  pocket: ["None", "Single Patch", "Double Flap"],
  lapel: ["Notch", "Peak", "Shawl"],
  vent: ["Single", "Double", "None"],
  lining: ["Standard", "Silk Patterned", "Contrast"],
};

export default function Bespoke() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [fabrics, setFabrics] = useState([]);
  const [fabric, setFabric] = useState(null);
  const [garment, setGarment] = useState(params.get("garment") || "shirt");
  const [meas, setMeas] = useState({ profile_name: "Self", body_type: "athletic", neck: "", chest: "", waist: "", shoulder: "", sleeve: "" });
  const [style, setStyle] = useState({ fit: "Tailored", collar: "Spread", cuff: "Single Button", button: "Horn", pocket: "None", lapel: "Notch", vent: "Single", lining: "Standard", monogram: "" });
  const [address, setAddress] = useState({ label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "" });

  useEffect(() => {
    api.get("/fabrics").then(({ data }) => {
      const list = data || [];
      setFabrics(list);
      const fid = params.get("fabric");
      if (fid) setFabric(list.find((f) => f.id === fid));
    }).catch(() => {});
  }, [params]);

  const fabricCost = fabric?.price || 0;
  const tailoring = GARMENTS.find((g) => g.k === garment).base;
  const subtotal = fabricCost + tailoring;
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + tax;
  const advance = Math.round(total * 0.5);

  const submit = async () => {
    if (!user) { toast("Sign in to place order"); nav("/auth"); return; }
    try {
      const { data } = await api.post("/bespoke/orders", {
        fabric_id: fabric?.id, garment_type: garment, measurement: meas, style,
        address,
      });
      toast.success(`Bespoke order ${data.id} placed. 50% advance paid (mock).`);
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order");
    }
  };

  return (
    <main className="sq-container py-12" data-testid="bespoke-wizard">
      <div className="sq-eyebrow text-squire-jeans">Bespoke Atelier</div>
      <h1 className="font-display text-5xl md:text-6xl mt-2">Begin your bespoke.</h1>
      <p className="text-[#4B5563] mt-3 max-w-xl">Six considered steps. A piece cut, stitched, and finished for you.</p>

      {/* Stepper */}
      <div className="mt-10 flex flex-wrap gap-3" data-testid="bespoke-stepper">
        {STEPS.map((s, i) => (
          <button
            key={s}
            data-testid={`bespoke-step-${i}`}
            onClick={() => setStep(i)}
            className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border ${i === step ? "border-squire-jeans bg-squire-jeans text-white" : i < step ? "border-squire-jeans text-squire-jeans" : "border-black/20 text-[#4B5563]"}`}
          >
            {i < step ? <Check size={14} /> : <span>{i + 1}</span>}
            <span>{s}</span>
          </button>
        ))}
      </div>

      <div className="mt-10 min-h-[420px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}>
            {step === 0 && (
              <div data-testid="bespoke-step-fabric">
                <h3 className="font-display text-3xl mb-6">Choose a Fabric</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                  {fabrics.map((f) => (
                    <button
                      key={f.id} data-testid={`bespoke-fabric-${f.id}`} onClick={() => setFabric(f)}
                      className={`text-left group border ${fabric?.id === f.id ? "border-squire-jeans" : "border-transparent hover:border-black"}`}
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img src={f.images?.[0]} alt={f.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-3">
                        <div className="font-display text-lg">{f.name}</div>
                        <div className="text-sm text-[#4B5563] mt-1">{formatINR(f.price)} / m</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {step === 1 && (
              <div data-testid="bespoke-step-garment">
                <h3 className="font-display text-3xl mb-6">Choose a Garment</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {GARMENTS.map((g) => (
                    <button key={g.k} data-testid={`bespoke-garment-${g.k}`} onClick={() => setGarment(g.k)} className={`p-6 text-left border ${garment === g.k ? "border-squire-jeans bg-[#F5F5F5]" : "border-black/20 hover:border-black"}`}>
                      <div className="font-display text-2xl">{g.label}</div>
                      <div className="sq-eyebrow mt-2">from {formatINR(g.base)}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {step === 2 && (
              <div data-testid="bespoke-step-meas">
                <h3 className="font-display text-3xl mb-6">Enter Measurements</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                  <Input label="Profile Name" v={meas.profile_name} onChange={(v) => setMeas({ ...meas, profile_name: v })} testid="meas-profile" />
                  <Select label="Body Type" v={meas.body_type} onChange={(v) => setMeas({ ...meas, body_type: v })} testid="meas-body" options={["slim", "athletic", "broad", "round"]} />
                  {["neck", "chest", "waist", "shoulder", "sleeve", "cuff", "shirt_length", "jacket_length", "trouser_inseam", "seat", "thigh", "knee"].map((k) => (
                    <Input key={k} label={k.replace("_", " ")} v={meas[k] || ""} onChange={(v) => setMeas({ ...meas, [k]: v })} testid={`meas-${k}`} suffix="in" />
                  ))}
                </div>
                <p className="text-xs text-[#4B5563] mt-4">First time? Watch our guided measurement video in your dashboard after sign-in.</p>
              </div>
            )}
            {step === 3 && (
              <div data-testid="bespoke-step-style">
                <h3 className="font-display text-3xl mb-6">Style Customisation</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {Object.entries(STYLE_OPTIONS).map(([k, opts]) => (
                    <Select key={k} label={k} v={style[k]} onChange={(v) => setStyle({ ...style, [k]: v })} testid={`style-${k}`} options={opts} />
                  ))}
                  <Input label="Monogram (optional)" v={style.monogram} onChange={(v) => setStyle({ ...style, monogram: v })} testid="style-monogram" />
                </div>
              </div>
            )}
            {step === 4 && (
              <div data-testid="bespoke-step-pricing">
                <h3 className="font-display text-3xl mb-6">Pricing Summary</h3>
                <div className="max-w-lg border border-black/10 p-6 space-y-3">
                  <Row label="Fabric" value={formatINR(fabricCost)} />
                  <Row label={`Tailoring (${garment})`} value={formatINR(tailoring)} />
                  <Row label="Tax (5%)" value={formatINR(tax)} />
                  <div className="border-t border-black/10 pt-3">
                    <Row label="Total" value={formatINR(total)} strong />
                    <Row label="Advance (50%)" value={formatINR(advance)} />
                    <Row label="Balance on delivery" value={formatINR(total - advance)} />
                  </div>
                  <div className="sq-eyebrow text-squire-jeans pt-3">Estimated delivery: 18 – 21 days</div>
                </div>
              </div>
            )}
            {step === 5 && (
              <div data-testid="bespoke-step-checkout">
                <h3 className="font-display text-3xl mb-6">Shipping & Checkout</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
                  <Input label="Address Line 1" v={address.line1} onChange={(v) => setAddress({ ...address, line1: v })} testid="ck-line1" />
                  <Input label="Address Line 2" v={address.line2} onChange={(v) => setAddress({ ...address, line2: v })} testid="ck-line2" />
                  <Input label="City" v={address.city} onChange={(v) => setAddress({ ...address, city: v })} testid="ck-city" />
                  <Input label="State" v={address.state} onChange={(v) => setAddress({ ...address, state: v })} testid="ck-state" />
                  <Input label="Pincode" v={address.pincode} onChange={(v) => setAddress({ ...address, pincode: v })} testid="ck-pin" />
                  <Input label="Phone" v={address.phone} onChange={(v) => setAddress({ ...address, phone: v })} testid="ck-phone" />
                </div>
                <button data-testid="bespoke-place-order" onClick={submit} className="sq-btn-primary mt-8">Pay 50% Advance (Razorpay mock) & Confirm</button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-10 flex justify-between">
        <button data-testid="bespoke-back" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="sq-btn-outline disabled:opacity-30">Back</button>
        {step < STEPS.length - 1 && <button data-testid="bespoke-next" onClick={() => setStep((s) => s + 1)} className="sq-btn-primary">Next Step</button>}
      </div>
    </main>
  );
}

function Input({ label, v, onChange, testid, suffix }) {
  return (
    <label className="block">
      <span className="sq-eyebrow">{label}</span>
      <div className="flex items-center gap-2 border-b border-black/30 focus-within:border-squire-jeans mt-2 pb-2">
        <input data-testid={testid} value={v} onChange={(e) => onChange(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" />
        {suffix && <span className="text-xs text-[#4B5563]">{suffix}</span>}
      </div>
    </label>
  );
}
function Select({ label, v, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="sq-eyebrow">{label}</span>
      <select data-testid={testid} value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-black/30 focus:border-squire-jeans py-2 outline-none text-sm mt-2">
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
function Row({ label, value, strong }) {
  return <div className={`flex justify-between ${strong ? "font-semibold text-lg" : ""}`}><span>{label}</span><span>{value}</span></div>;
}
